import { z } from 'zod';
/**
 * Отчёт по воронке.
 *
 * Вопрос, на который он отвечает, один: где теряются заявки. Не «сколько
 * их было» — это видно и без отчёта, — а на каком шаге из ста заявок
 * остаётся двадцать, и одинаково ли это на разных источниках.
 *
 * **Ступени не вложены одна в другую, и доля у каждой одна — от
 * заявок.** Сначала было иначе: я считал и «долю от предыдущей
 * ступени», исходя из того, что каждая следующая — часть прежней. На
 * первых же настоящих данных это дало 122%: «передали в колл-центр»
 * оказалось больше, чем «агент ответил». Ошибки в подсчёте не было —
 * так и было на самом деле: агент звал передачу, не написав клиенту ни
 * слова. Случай реальный, прятать его нельзя, а значит «конверсия из
 * ступени в ступень» здесь просто не существует как величина.
 *
 * Доля от заявок осмысленна всегда и никогда не превышает ста
 * процентов. А расхождения между ступенями отчёт называет словами
 * отдельной строкой — это находка, а не погрешность.
 *
 * Отдельная оговорка про «клиент ответил агенту». Первое сообщение
 * клиента — шаблон виджета, а не написанный им текст, поэтому сама
 * заявка ещё ничего не говорит о человеке на той стороне. Считается
 * входящее **после первого ответа агента**: именно между «агент
 * ответил» и «клиент ответил» теряется больше всего, и лечится это
 * разными вещами — первое инструкцией, второе качеством трафика.
 */
export const FUNNEL_STAGES = [
    'leads',
    'answered',
    'engaged',
    'handedToCallCenter',
    'ordered',
];
export const FUNNEL_STAGE_LABELS = {
    leads: 'Заявки',
    answered: 'Агент ответил',
    engaged: 'Клиент ответил агенту',
    handedToCallCenter: 'Передали в колл-центр',
    ordered: 'Заказ',
};
export const FUNNEL_STAGE_HINTS = {
    leads: 'Открытые диалоги за период — по одному на заявку с лендинга',
    answered: 'Агент отправил клиенту хотя бы одно сообщение',
    engaged: 'Клиент написал сам, после шаблона виджета. До этой ступени человека на той стороне ещё не видно',
    handedToCallCenter: 'Исход «передан в колл-центр»: клиент согласился на звонок',
    ordered: 'Исход «заказ». Проставляется после подтверждения заказа',
};
/** По чему разбивать строки отчёта. */
export const FUNNEL_GROUPINGS = ['none', 'subid', 'offerGroup', 'product'];
export const FUNNEL_GROUPING_LABELS = {
    none: 'Без разбивки',
    subid: 'По источникам',
    offerGroup: 'По группам офферов',
    product: 'По товарам',
};
export const funnelQuerySchema = z.object({
    /**
     * Границы периода — по дате создания диалога, а не исхода. Заявка,
     * пришедшая вчера и купившая сегодня, считается вчерашней: иначе
     * конверсия периода складывалась бы из разных когорт и в узком окне
     * могла бы превысить сто процентов.
     */
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    groupBy: z.enum(FUNNEL_GROUPINGS).default('none'),
    channelId: z.uuid().optional(),
});
export const funnelRowSchema = z.object({
    /** Значение разбивки: subid, группа оффера, идентификатор товара. */
    key: z.string().nullable(),
    /** Что показать человеку: название товара или сам subid. */
    label: z.string(),
    leads: z.number().int().nonnegative(),
    answered: z.number().int().nonnegative(),
    engaged: z.number().int().nonnegative(),
    handedToCallCenter: z.number().int().nonnegative(),
    ordered: z.number().int().nonnegative(),
    /**
     * Явные отказы. В воронку не входят: это не ступень, а ветка в
     * сторону, и складывать её с «заказом» нельзя. Но знать полезно —
     * отказ отличается от молчания, и лечатся они разным.
     */
    refused: z.number().int().nonnegative(),
});
export const funnelReportSchema = z.object({
    rows: z.array(funnelRowSchema),
    total: funnelRowSchema,
    from: z.iso.datetime().nullable(),
    to: z.iso.datetime().nullable(),
    groupBy: z.enum(FUNNEL_GROUPINGS),
});
/**
 * Доля в процентах, от какого угодно основания.
 *
 * Ноль в основании даёт `null`, а не ноль процентов: «ни одной заявки»
 * и «сто заявок, ни одного заказа» — разные новости, и показывать их
 * одинаковым «0%» значит скрывать первую.
 */
export function share(part, whole) {
    if (whole <= 0)
        return null;
    return Math.round((part / whole) * 1_000) / 10;
}
/**
 * Что в отчёте не сходится — словами.
 *
 * Не проверка данных, а находка. Ступени не вложены, и расхождение
 * между ними означает поведение, о котором стоит знать: заявка, ушедшая
 * в колл-центр без единого слова от агента, для клиента выглядит как
 * звонок с улицы.
 */
export function funnelNotes(row) {
    const notes = [];
    if (row.handedToCallCenter > row.answered) {
        notes.push(`Передано в колл-центр больше, чем диалогов с ответом агента: ${row.handedToCallCenter} против ${row.answered}. ` +
            'Значит часть заявок агент передаёт, не написав клиенту ни слова. Лечится правилом в инструкции: ' +
            'не звать передачу на первом сообщении.');
    }
    if (row.ordered > row.handedToCallCenter) {
        notes.push(`Заказов больше, чем передач в колл-центр: ${row.ordered} против ${row.handedToCallCenter}. ` +
            'Обычно это значит, что исход проставили руками, минуя передачу.');
    }
    return notes;
}
//# sourceMappingURL=reports.js.map